import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, Edit, X, Save, Plus, Trash2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KlubbenPage = () => {
  const [showMenuCard, setShowMenuCard] = useState(false);
  const [cms, setCms] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [showLayoutPanel, setShowLayoutPanel] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { isModerator, getToken } = useAuth();

  useEffect(() => {
    axios.get(`${API}/settings/klubben-content`).then(r => setCms(r.data)).catch(() => {});
  }, []);

  if (!cms) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-gray-500">Indlæser…</div>;
  }

  const sectionMap = (cms.sections || []).reduce((acc, s) => { acc[s.key] = s; return acc; }, {});
  const sectionVisible = (key) => sectionMap[key]?.visible !== false;
  const sectionOrder = (key, fallback) => sectionMap[key]?.order ?? fallback;

  const persist = async (patch) => {
    try {
      const token = getToken();
      const res = await axios.put(`${API}/settings/klubben-content`, patch, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCms(res.data);
      return res.data;
    } catch { toast.error("Kunne ikke gemme"); return null; }
  };

  const moveSection = async (key, direction) => {
    const sorted = [...(cms.sections || [])].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex(s => s.key === key);
    if (idx < 0) return;
    const swap = idx + direction;
    if (swap < 0 || swap >= sorted.length) return;
    [sorted[idx], sorted[swap]] = [sorted[swap], sorted[idx]];
    await persist({ sections: sorted.map((s, i) => ({ ...s, order: i })) });
  };

  const toggleSectionVisibility = async (key) => {
    const updated = (cms.sections || []).map(s => s.key === key ? { ...s, visible: !s.visible } : s);
    await persist({ sections: updated });
  };

  const handleSave = async () => {
    const res = await persist(form);
    if (res) {
      setEditing(false);
      toast.success("Gemt!");
    }
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = getToken();
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/settings/klubben-content/upload-image`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const fullUrl = res.data.url.startsWith("http") ? res.data.url : `${BACKEND_URL}${res.data.url}`;
      setForm(p => ({ ...p, [field]: fullUrl }));
      toast.success("Billede uploadet!");
    } catch {
      toast.error("Kunne ikke uploade");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const updateMenuItem = (i, field, value) => {
    const items = [...(form.menu_items || cms.menu_items)];
    items[i] = { ...items[i], [field]: value };
    setForm(p => ({ ...p, menu_items: items }));
  };
  const addMenuItem = () => {
    const items = [...(form.menu_items || cms.menu_items)];
    items.push({ icon: "🍴", title: "Ny", description: "", highlight: false, link: "" });
    setForm(p => ({ ...p, menu_items: items }));
  };
  const removeMenuItem = (i) => {
    const items = [...(form.menu_items || cms.menu_items)];
    items.splice(i, 1);
    setForm(p => ({ ...p, menu_items: items }));
  };

  const updateOpeningHour = (i, field, value) => {
    const hrs = [...(form.opening_hours || cms.opening_hours)];
    hrs[i] = { ...hrs[i], [field]: value };
    setForm(p => ({ ...p, opening_hours: hrs }));
  };
  const addOpeningHour = () => {
    const hrs = [...(form.opening_hours || cms.opening_hours)];
    hrs.push({ day: "Dag", hours: "17:00 – 22:00", open: true });
    setForm(p => ({ ...p, opening_hours: hrs }));
  };
  const removeOpeningHour = (i) => {
    const hrs = [...(form.opening_hours || cms.opening_hours)];
    hrs.splice(i, 1);
    setForm(p => ({ ...p, opening_hours: hrs }));
  };

  const SectionControls = ({ sectionKey }) => {
    if (!isModerator) return null;
    const s = sectionMap[sectionKey];
    return (
      <div
        className="absolute top-2 right-2 z-30 flex items-center gap-1 bg-black/85 backdrop-blur-sm border border-purple-500/40 rounded-lg p-1 shadow-lg"
        data-testid={`klubben-section-controls-${sectionKey}`}
      >
        <button onClick={() => moveSection(sectionKey, -1)} className="p-1.5 rounded text-white hover:bg-purple-600/30">↑</button>
        <button onClick={() => moveSection(sectionKey, +1)} className="p-1.5 rounded text-white hover:bg-purple-600/30">↓</button>
        <button
          onClick={() => toggleSectionVisibility(sectionKey)}
          className={`p-1.5 rounded ${s?.visible === false ? "text-red-400 bg-red-500/20" : "text-white hover:bg-purple-600/30"}`}
        >{s?.visible === false ? "👁‍🗨" : "👁"}</button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black flex flex-col" data-testid="klubben-page">
      {/* Floating admin controls */}
      {isModerator && (
        <>
          <button
            onClick={() => { setForm({ ...cms }); setEditing(true); }}
            className="fixed bottom-5 right-5 z-50 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2.5 rounded-full shadow-2xl flex items-center gap-2 text-sm font-bold font-['Oswald'] uppercase tracking-wider"
            style={{ boxShadow: "0 0 20px rgba(59, 130, 246, 0.6)" }}
            data-testid="klubben-edit-btn"
          >
            <Edit size={16} /> Rediger
          </button>
          <button
            onClick={() => setShowLayoutPanel(p => !p)}
            className="fixed bottom-5 right-36 z-50 bg-purple-600 hover:bg-purple-500 text-white px-4 py-2.5 rounded-full shadow-2xl flex items-center gap-2 text-sm font-bold font-['Oswald'] uppercase tracking-wider"
            style={{ boxShadow: "0 0 20px rgba(168, 85, 247, 0.6)" }}
            data-testid="klubben-layout-toggle"
          >
            ✏ Layout
          </button>
        </>
      )}

      {/* Layout panel */}
      {isModerator && showLayoutPanel && (
        <div className="fixed inset-y-0 right-0 z-40 w-80 max-w-[90vw] bg-neutral-950/98 border-l border-purple-500/30 shadow-2xl overflow-y-auto p-4" data-testid="klubben-layout-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-['Oswald'] font-bold uppercase tracking-wider text-sm">Klubben sektioner</h3>
            <button onClick={() => setShowLayoutPanel(false)} className="text-gray-400 hover:text-white text-2xl">×</button>
          </div>
          <ul className="space-y-2">
            {[...(cms.sections || [])].sort((a, b) => a.order - b.order).map((s, idx) => (
              <li key={s.key} className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-lg p-2">
                <span className="text-gray-500 text-xs w-6">{idx + 1}</span>
                <span className={`flex-1 text-sm ${s.visible === false ? "text-gray-500 line-through" : "text-white"}`}>{s.label}</span>
                <button onClick={() => moveSection(s.key, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-white/10 disabled:opacity-30">↑</button>
                <button onClick={() => moveSection(s.key, +1)} disabled={idx === (cms.sections.length - 1)} className="p-1 rounded hover:bg-white/10 disabled:opacity-30">↓</button>
                <button onClick={() => toggleSectionVisibility(s.key)} className={`p-1 rounded text-xs ${s.visible === false ? "text-red-400" : "text-emerald-400 hover:bg-white/10"}`}>{s.visible === false ? "Skjult" : "Synlig"}</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/85 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) setEditing(false); }} data-testid="klubben-edit-modal">
          <div className="min-h-full flex items-start justify-center p-3">
            <div className="my-6 w-full max-w-2xl bg-neutral-950/98 border border-blue-500/30 rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-['Oswald'] font-bold text-white">Rediger Klubben-side</h2>
                <button onClick={() => setEditing(false)} className="text-gray-400 hover:text-white text-2xl">×</button>
              </div>

              {/* Hero */}
              <fieldset className="border border-white/10 rounded-lg p-3 space-y-2">
                <legend className="px-2 text-xs text-blue-300 uppercase tracking-wider">Hero</legend>
                <label className="block text-xs text-gray-400">Baggrundsbillede</label>
                {form.hero_bg_image && <img src={form.hero_bg_image} alt="" className="max-h-32 rounded mb-1 border border-white/10" />}
                <label className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs cursor-pointer">
                  {uploading ? "Uploader..." : "Upload billede"}
                  <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, "hero_bg_image")} className="hidden" />
                </label>
                <input value={form.hero_bg_image || ""} onChange={e => setForm(p => ({ ...p, hero_bg_image: e.target.value }))} placeholder="…eller URL" className="mc-input w-full text-xs" />
                <label className="block text-xs text-gray-400 mt-2">Titel</label>
                <input value={form.hero_title || ""} onChange={e => setForm(p => ({ ...p, hero_title: e.target.value }))} className="mc-input w-full" data-testid="klubben-hero-title-input" />
                <label className="block text-xs text-gray-400 mt-2">Undertekst</label>
                <textarea value={form.hero_subtitle || ""} onChange={e => setForm(p => ({ ...p, hero_subtitle: e.target.value }))} rows={2} className="mc-input w-full resize-none" />
              </fieldset>

              {/* Opening hours */}
              <fieldset className="border border-white/10 rounded-lg p-3 space-y-2">
                <legend className="px-2 text-xs text-blue-300 uppercase tracking-wider">Åbningstider</legend>
                {(form.opening_hours || []).map((h, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input value={h.day} onChange={e => updateOpeningHour(i, "day", e.target.value)} placeholder="Dag" className="mc-input flex-1 text-sm" />
                    <input value={h.hours} onChange={e => updateOpeningHour(i, "hours", e.target.value)} placeholder="17:00 – 22:00" className="mc-input flex-1 text-sm" />
                    <button onClick={() => removeOpeningHour(i)} className="text-red-400 p-1.5 hover:bg-red-500/20 rounded"><Trash2 size={14} /></button>
                  </div>
                ))}
                <button onClick={addOpeningHour} className="text-emerald-400 text-xs flex items-center gap-1 hover:underline"><Plus size={12} /> Tilføj åbningstid</button>
                <label className="block text-xs text-gray-400 mt-2">Note under listen</label>
                <input value={form.opening_hours_note || ""} onChange={e => setForm(p => ({ ...p, opening_hours_note: e.target.value }))} className="mc-input w-full text-sm" />
              </fieldset>

              {/* Menu items */}
              <fieldset className="border border-white/10 rounded-lg p-3 space-y-2">
                <legend className="px-2 text-xs text-blue-300 uppercase tracking-wider">Mad & drikke</legend>
                {(form.menu_items || []).map((m, i) => (
                  <div key={i} className="border border-white/5 rounded p-2 space-y-1">
                    <div className="flex gap-2 items-center">
                      <input value={m.icon} onChange={e => updateMenuItem(i, "icon", e.target.value)} placeholder="🍴" className="mc-input w-16 text-center" />
                      <input value={m.title} onChange={e => updateMenuItem(i, "title", e.target.value)} placeholder="Titel" className="mc-input flex-1 text-sm" />
                      <button onClick={() => removeMenuItem(i)} className="text-red-400 p-1.5 hover:bg-red-500/20 rounded"><Trash2 size={14} /></button>
                    </div>
                    <textarea value={m.description} onChange={e => updateMenuItem(i, "description", e.target.value)} placeholder="Beskrivelse" rows={1} className="mc-input w-full text-sm resize-none" />
                    <div className="flex gap-2">
                      <input value={m.link || ""} onChange={e => updateMenuItem(i, "link", e.target.value)} placeholder="Link (valgfri)" className="mc-input flex-1 text-xs" />
                      <label className="text-xs text-gray-400 flex items-center gap-1">
                        <input type="checkbox" checked={!!m.highlight} onChange={e => updateMenuItem(i, "highlight", e.target.checked)} /> Fremhæv
                      </label>
                    </div>
                  </div>
                ))}
                <button onClick={addMenuItem} className="text-emerald-400 text-xs flex items-center gap-1 hover:underline"><Plus size={12} /> Tilføj punkt</button>
              </fieldset>

              {/* Pris */}
              <fieldset className="border border-white/10 rounded-lg p-3 space-y-2">
                <legend className="px-2 text-xs text-blue-300 uppercase tracking-wider">Pris-highlight</legend>
                <input value={form.price_label || ""} onChange={e => setForm(p => ({ ...p, price_label: e.target.value }))} placeholder="Label" className="mc-input w-full text-sm" />
                <div className="flex gap-2">
                  <input value={form.price_amount || ""} onChange={e => setForm(p => ({ ...p, price_amount: e.target.value }))} placeholder="60" className="mc-input flex-1 text-sm" />
                  <input value={form.price_currency || ""} onChange={e => setForm(p => ({ ...p, price_currency: e.target.value }))} placeholder="KR" className="mc-input w-20 text-sm" />
                </div>
                <input value={form.price_note || ""} onChange={e => setForm(p => ({ ...p, price_note: e.target.value }))} placeholder="Note" className="mc-input w-full text-sm" />
              </fieldset>

              {/* Atmosphere */}
              <fieldset className="border border-white/10 rounded-lg p-3 space-y-2">
                <legend className="px-2 text-xs text-blue-300 uppercase tracking-wider">Stemningen</legend>
                <input value={form.atmosphere_title || ""} onChange={e => setForm(p => ({ ...p, atmosphere_title: e.target.value }))} placeholder="Titel" className="mc-input w-full" />
                <textarea value={form.atmosphere_text || ""} onChange={e => setForm(p => ({ ...p, atmosphere_text: e.target.value }))} rows={4} className="mc-input w-full text-sm resize-none" />
              </fieldset>

              {/* Menukort */}
              <fieldset className="border border-white/10 rounded-lg p-3 space-y-2">
                <legend className="px-2 text-xs text-blue-300 uppercase tracking-wider">Menukort-billede</legend>
                {form.menukort_url && <img src={form.menukort_url} alt="" className="max-h-32 rounded mb-1 border border-white/10" />}
                <label className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs cursor-pointer">
                  {uploading ? "Uploader..." : "Upload menukort"}
                  <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, "menukort_url")} className="hidden" />
                </label>
                <input value={form.menukort_url || ""} onChange={e => setForm(p => ({ ...p, menukort_url: e.target.value }))} placeholder="…eller URL" className="mc-input w-full text-xs" />
              </fieldset>

              <div className="flex gap-2 pt-2">
                <button onClick={handleSave} className="chrome-btn px-4 py-2 rounded-lg flex items-center gap-2" data-testid="klubben-save-btn"><Save size={16} /> Gem ændringer</button>
                <button onClick={() => setEditing(false)} className="ghost-blue-btn px-4 py-2 rounded-lg flex items-center gap-2"><X size={16} /> Annuller</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== HERO ===== */}
      {sectionVisible("hero") && (
        <section className="relative h-[75vh] min-h-[550px] overflow-hidden" style={{ order: sectionOrder("hero", 0) }}>
          <SectionControls sectionKey="hero" />
          <div className="absolute inset-0" style={{ backgroundImage: `url('${cms.hero_bg_image}')`, backgroundSize: "cover", backgroundPosition: "center 20%" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.2) 40%, rgba(0,0,0,0.4) 70%, rgba(0,0,0,1) 100%)" }} />
          <div className="absolute top-24 sm:top-28 left-0 right-0 px-6 text-center">
            <h1 className="text-5xl sm:text-7xl font-['Oswald'] font-bold text-white mb-4" style={{ textShadow: "3px 3px 6px rgba(0,0,0,0.9), 0 0 30px rgba(0,0,0,0.8)" }} data-testid="klubben-hero-title">{cms.hero_title}</h1>
            <p className="text-lg sm:text-xl text-white font-medium max-w-2xl mx-auto" style={{ textShadow: "2px 2px 4px rgba(0,0,0,0.9), 0 0 20px rgba(0,0,0,0.8)" }}>{cms.hero_subtitle}</p>
          </div>
        </section>
      )}

      {/* ===== HOURS ===== */}
      {sectionVisible("hours") && (
        <section className="relative bg-black px-6 sm:px-12 py-16" style={{ order: sectionOrder("hours", 1) }}>
          <SectionControls sectionKey="hours" />
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-['Oswald'] font-bold text-white mb-3"><span className="text-blue-400">🕐</span> ÅBNINGSTIDER</h2>
              <p className="text-gray-400">Kom forbi og vær en del af fællesskabet</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {(cms.opening_hours || []).filter(h => h.open).map((item, idx) => (
                <div key={idx} className="public-card p-6 text-center" style={{ background: "radial-gradient(ellipse at center, rgba(20, 83, 45, 0.4) 0%, rgba(15, 60, 35, 0.3) 50%, rgba(5, 5, 5, 1) 100%)" }}>
                  <div className="text-green-400 text-sm font-medium mb-1">ÅBEN</div>
                  <div className="text-2xl font-['Oswald'] font-bold text-white mb-2">{item.day}</div>
                  <div className="text-xl text-green-300 font-medium">{item.hours}</div>
                </div>
              ))}
            </div>
            {cms.opening_hours_note && <p className="text-center text-gray-500 mt-6 text-sm">{cms.opening_hours_note}</p>}
          </div>
        </section>
      )}

      {/* ===== FOOD ===== */}
      {sectionVisible("food") && (
        <section className="relative bg-black px-6 sm:px-12 py-16" style={{ order: sectionOrder("food", 2) }}>
          <SectionControls sectionKey="food" />
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-['Oswald'] font-bold text-white mb-3"><span className="text-yellow-400">🍴</span> MAD & DRIKKE</h2>
              <p className="text-gray-400">God mad til gode priser</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              {(cms.menu_items || []).map((item, index) => {
                const cardContent = (
                  <div className="flex items-start gap-4">
                    <span className="text-4xl">{item.icon}</span>
                    <div className="flex-1">
                      <h3 className={`text-xl font-bold mb-1 ${item.highlight ? "text-yellow-300" : "text-white"}`}>{item.title}</h3>
                      <p className="text-gray-300">{item.description}</p>
                      {item.title === "À la carte" && (
                        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowMenuCard(true); }} className="mt-4 bg-white/10 hover:bg-white/20 border border-white/20 px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2 text-white transition-all" data-testid="menu-card-btn">
                          <span>📋</span> Se menukort
                        </button>
                      )}
                    </div>
                  </div>
                );
                return item.link ? (
                  <Link key={index} to={item.link} className="public-card p-6 transition-all hover:scale-[1.02] block" style={item.highlight ? { background: "radial-gradient(ellipse at center, rgba(120, 80, 20, 0.5) 0%, rgba(80, 50, 10, 0.3) 50%, rgba(5, 5, 5, 1) 100%)" } : undefined}>{cardContent}</Link>
                ) : (
                  <div key={index} className="public-card p-6 transition-all hover:scale-[1.02]" style={item.highlight ? { background: "radial-gradient(ellipse at center, rgba(120, 80, 20, 0.5) 0%, rgba(80, 50, 10, 0.3) 50%, rgba(5, 5, 5, 1) 100%)" } : undefined}>{cardContent}</div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ===== PRICE ===== */}
      {sectionVisible("price") && (
        <section className="relative bg-black px-6 sm:px-12 py-12" style={{ order: sectionOrder("price", 3) }}>
          <SectionControls sectionKey="price" />
          <div className="text-center">
            <div className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 sm:p-10">
              <div className="text-white text-lg mb-2">{cms.price_label}</div>
              <div className="text-6xl sm:text-7xl font-['Oswald'] font-bold text-white mb-2">{cms.price_amount} <span className="text-3xl">{cms.price_currency}</span></div>
              <div className="text-blue-200 text-sm">{cms.price_note}</div>
            </div>
          </div>
        </section>
      )}

      {/* ===== ATMOSPHERE ===== */}
      {sectionVisible("atmosphere") && (
        <section className="relative bg-black px-6 sm:px-12 py-12" style={{ order: sectionOrder("atmosphere", 4) }}>
          <SectionControls sectionKey="atmosphere" />
          <div className="max-w-5xl mx-auto text-center">
            <div className="public-card p-8 sm:p-12">
              <h2 className="text-2xl sm:text-3xl font-['Oswald'] font-bold text-white mb-4">{cms.atmosphere_title}</h2>
              <p className="text-gray-300 text-lg leading-relaxed max-w-2xl mx-auto mb-6 whitespace-pre-wrap">{cms.atmosphere_text}</p>
              <div className="flex justify-center gap-4 text-4xl">
                <span>🍺</span><span>🏍️</span><span>🤝</span><span>🎸</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ===== CTA ===== */}
      {sectionVisible("cta") && (
        <section className="relative bg-black px-6 sm:px-12 py-12" style={{ order: sectionOrder("cta", 5) }}>
          <SectionControls sectionKey="cta" />
          <div className="text-center">
            <Link to="/kontakt-os" className="radial-btn px-8 py-4 rounded-xl text-lg inline-flex items-center gap-3"><MapPin className="w-5 h-5" /> Find vej til klubben</Link>
          </div>
        </section>
      )}

      {/* Menukort modal */}
      {showMenuCard && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" onClick={() => setShowMenuCard(false)}>
          <div className="relative max-w-4xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowMenuCard(false)} className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full w-12 h-12 flex items-center justify-center text-2xl">×</button>
            {cms.menukort_url && <img src={cms.menukort_url} alt="Menukort" className="w-full h-auto rounded-xl" />}
          </div>
        </div>
      )}
    </div>
  );
};

export default KlubbenPage;
